import i18next from 'i18next';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ReportDateRangeParameters } from '@/api/reports/reportsApi';
import {
  fetchReportsSummary,
  fetchTreatmentsDelivered,
  fetchVisitsByStatus,
  fetchVisitsByType,
} from '@/api/reports/reportsApi';
import type {
  ReportsSummary,
  TreatmentsDeliveredRow,
  VisitsByStatusRow,
  VisitsByTypeRow,
} from '@/api/reports/reportsApi.types';
import {
  REPORTS_SUMMARY_QUERY_KEY,
  REPORTS_TREATMENTS_DELIVERED_QUERY_KEY,
  REPORTS_VISITS_BY_STATUS_QUERY_KEY,
  REPORTS_VISITS_BY_TYPE_QUERY_KEY,
} from '@/constants/queryKeys';

export function useReportsSummaryQuery(
  dateRange: ReportDateRangeParameters
): UseQueryResult<ReportsSummary, Error> {
  return useQuery({
    queryKey: [...REPORTS_SUMMARY_QUERY_KEY, dateRange.dateFrom, dateRange.dateTo],
    queryFn: function loadSummary() {
      return fetchReportsSummary(dateRange);
    },
    placeholderData: keepPreviousData,
  });
}

export function useVisitsByTypeQuery(
  dateRange: ReportDateRangeParameters
): UseQueryResult<VisitsByTypeRow[], Error> {
  return useQuery({
    queryKey: [
      ...REPORTS_VISITS_BY_TYPE_QUERY_KEY,
      dateRange.dateFrom,
      dateRange.dateTo,
      i18next.language,
    ],
    queryFn: function loadVisitsByType() {
      return fetchVisitsByType(dateRange);
    },
    placeholderData: keepPreviousData,
  });
}

export function useVisitsByStatusQuery(
  dateRange: ReportDateRangeParameters
): UseQueryResult<VisitsByStatusRow[], Error> {
  return useQuery({
    queryKey: [
      ...REPORTS_VISITS_BY_STATUS_QUERY_KEY,
      dateRange.dateFrom,
      dateRange.dateTo,
      i18next.language,
    ],
    queryFn: function loadVisitsByStatus() {
      return fetchVisitsByStatus(dateRange);
    },
    placeholderData: keepPreviousData,
  });
}

export function useTreatmentsDeliveredQuery(
  dateRange: ReportDateRangeParameters
): UseQueryResult<TreatmentsDeliveredRow[], Error> {
  return useQuery({
    queryKey: [
      ...REPORTS_TREATMENTS_DELIVERED_QUERY_KEY,
      dateRange.dateFrom,
      dateRange.dateTo,
      i18next.language,
    ],
    queryFn: function loadTreatmentsDelivered() {
      return fetchTreatmentsDelivered(dateRange);
    },
    placeholderData: keepPreviousData,
  });
}
